/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import iconSet from '../../shared/images/teambuildr-selection.json';
import getAllUrlParams from '../../shared/utils/getAllUrlParams';
import Header from './components/presentational/Header';
import CalendarContent from './components/presentational/CalendarContent';

// Fresh key (vs. the retired tb_calendar_info_banner_dismissed) so coaches who
// dismissed the old parity banner still see the sunset announcement.
const BANNER_KEY = 'tb_calendar_sunset_banner_dismissed';
const LEGACY_CALENDAR_SUNSET_DATE = 'September 30, 2026';
const SUPPORT_EMAIL = 'support@teambuildr.com';

const getInitialBannerDismissed = () => {
  try {
    return localStorage.getItem(BANNER_KEY) === 'true';
  } catch (error) {
    return false;
  }
};

const persistBannerDismissed = () => {
  try {
    localStorage.setItem(BANNER_KEY, 'true');
  } catch (error) {
    // ignore — Safari private mode, blocked iframes, enterprise policy, etc.
  }
};

const PageWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  margin-top: ${(props) => (props.hideNav || props.viewType === 'weekView' ? '0px' : '49px')};
  padding-top: ${(props) => (!props.hideNav && props.viewType === 'weekView' ? '49px' : '')};
  height: ${(props) => (props.hideNav || props.viewType === 'weekView' ? '100%' : 'calc(100% - 49px)')};
  width: 100%;
  background: ${({ theme }) => theme.colors.backgroundApp};
  color: ${({ theme }) => theme.colors.text};
`;

const IconSideBarAndContentWrapper = styled('div')`
  display: flex;
  position: relative;
  height: ${(props) => {
    if (props.viewType === 'weekView') return '100%';
    if (props.isTvMode) return '100%';
    if (props.headerHeight) return `calc(100% - ${props.headerHeight}px)`;
    return 'calc(100% - 61.5px)';
  }};
  padding-top: ${(props) => (props.viewType === 'weekView' && !props.isTvMode ? `${props.headerHeight}px` : '')};
  ${(props) => props.viewType === 'weekView' && 'min-width: fit-content;'}
`;

const InfoBanner = styled('div')`
  position: fixed;
  top: ${(props) => (props.hideNav ? '87px' : '136px')};
  left: calc(50% + 24px);
  transform: translateX(-50%);
  z-index: 100;
  width: max-content;
  max-width: calc(100vw - 80px);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px 12px 16px;
  background: ${({ theme }) => theme.colors.warningSurface};
  border: 1px solid ${({ theme }) => theme.colors.warningBorder};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.warningText};
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.3px;
  line-height: 1.4;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);
`;

const BannerIconWrapper = styled('span')`
  display: inline-flex;
  flex-shrink: 0;

  @media screen and (max-width: 767px) {
    display: none;
  }
`;

// the seemingly redundant hover declarations counter the global anchor rule,
// which forces 16px text and an orange no-underline hover
const BannerLink = styled('a')`
  color: #1787FB;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  text-decoration: underline;
  transition: opacity 150ms ease-in-out;
  &:hover {
    color: #1787FB;
    text-decoration: underline;
    opacity: 0.7;
  }
`;

const BannerDismiss = styled('button')`
  position: absolute;
  top: -12px;
  right: -12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: ${({ theme }) => theme.colors.surface};
  border: 2px solid #B0B0B0;
  border-radius: 50%;
  cursor: pointer;
`;

const CalendarLayout = ({ flags }) => {
  const [bannerDismissed, setBannerDismissed] = useState(getInitialBannerDismissed);
  const headerHeight = useSelector((state) => state.calendar.data.headerHeight);
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const isTvMode = useSelector((state) => state.calendar.ui.isTvMode);
  const urlParams = getAllUrlParams(window.location.href, false);
  const hideNav = urlParams.hideNav === 'true' || isTvMode;
  const showSunsetBanner = Boolean(flags?.legacyCalendarSunsetBanner);

  const handleDismissBanner = () => {
    setBannerDismissed(true);
    persistBannerDismissed();
  };

  return (
    <PageWrapper viewType={viewType} hideNav={hideNav}>
      {!isTvMode && <Header />}
      {!isTvMode && showSunsetBanner && !bannerDismissed && (
        <InfoBanner hideNav={hideNav}>
          <BannerIconWrapper>
            <IcomoonReact iconSet={iconSet} size={18} icon='warning' color='#F59E0B' />
          </BannerIconWrapper>
          <span>
            {`Legacy Calendar is going away on ${LEGACY_CALENDAR_SUNSET_DATE}. Switch to Build to get started with the latest features and programming tools. Questions? Email `}
            <BannerLink href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</BannerLink>
            .
          </span>
          <BannerDismiss type='button' onClick={handleDismissBanner} aria-label='Dismiss banner'>
            <IcomoonReact iconSet={iconSet} size={8} icon='remove' color='#B0B0B0' aria-hidden='true' />
          </BannerDismiss>
        </InfoBanner>
      )}
      <IconSideBarAndContentWrapper
        headerHeight={headerHeight}
        viewType={viewType}
        isTvMode={isTvMode}
      >
        <CalendarContent />
      </IconSideBarAndContentWrapper>
    </PageWrapper>
  );
};

CalendarLayout.propTypes = {
  flags: PropTypes.shape({
    legacyCalendarSunsetBanner: PropTypes.bool,
  }),
};

CalendarLayout.defaultProps = {
  flags: {},
};

export default withLDConsumer()(CalendarLayout);
